import { HttpResourceRef } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IApiErrorResponse } from '@core/models/api-response.model';
import { IActiveTokensData, IRevokeTokenRequest } from '@core/models/active-tokens.model';
import { ApiService } from '@core/services/api/api.service';
import { Console } from './logger.service';

/**
 * Servizio per la gestione dei token attivi (sessioni).
 *
 * Questo servizio si occupa di recuperare l'elenco delle sessioni attive
 * e di revocare singoli token o tutti i token associati all'utenza.
 *
 * @example
 * constructor(private activeTokensService: ActiveTokensService) {}
 *
 * // Recupera i token attivi
 * this.activeTokensService.getActiveTokens().subscribe({
 *   next: (tokens) => console.log('Token attivi:', tokens),
 *   error: (error) => console.error('Errore:', error)
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class ActiveTokensService {
  private readonly log_label = 'ActiveTokensService';
  private readonly apiService = inject(ApiService);

  /**
   * Resource reattiva per l'elenco token attivi (Angular 22 `httpResource`).
   */
  createActiveTokensResource(shouldLoad: () => boolean): HttpResourceRef<IActiveTokensData> {
    return this.apiService.getResource<IActiveTokensData>(
      () => (shouldLoad() ? 'active-tokens' : undefined),
      undefined,
      { defaultValue: [], debugName: 'activeTokens' }
    );
  }

  /**
   * Recupera l'elenco dei token attivi (sessioni) dell'utente.
   *
   * @returns Observable contenente una lista di token attivi
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  getActiveTokens(): Observable<IActiveTokensData> {
    const log_pref = this.log_label + '.getActiveTokens() ';
    return this.apiService.get<IActiveTokensData>('active-tokens').pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel recupero dei token attivi.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Revoca un singolo token (sessione).
   *
   * @param tokenId L'ID del token da revocare
   * @returns Observable contenente il messaggio di successo
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  revokeToken(tokenId: number): Observable<string> {
    const log_pref = this.log_label + '.revokeToken() ';
    const request: IRevokeTokenRequest = { token_id: tokenId };
    return this.apiService.post<string, IRevokeTokenRequest>('revoke-token', request).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella revoca del token.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Revoca tutti i token (sessioni) associati all'utenza.
   *
   * @returns Observable contenente il messaggio di successo
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  revokeAllTokens(): Observable<string> {
    const log_pref = this.log_label + '.revokeAllTokens() ';
    return this.apiService.post<string, void>('logout-all', undefined).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella revoca di tutti i token.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }
}
